import { z } from 'zod';

import { COMPANY_NAME_MAX_LENGTH } from '@/constants';
import { urlHostValidation } from '@/helpers/url-domain-validation';
import i18n from '@/lib/i18n/i18n';

const { t } = i18n;

const KEY_OFFERING_MAX_LENGTH = 150;

// LinkedIn, including its lnkd.in short links.
const LINKEDIN_URL_HOSTS = ['linkedin.com', 'lnkd.in'];

// X (formerly Twitter): keep twitter.com so saved links stay valid.
const X_URL_HOSTS = ['x.com', 'twitter.com'];

interface EditCompanyHeaderValidationSchemaOptions {
  /** Saved X URL. Only a changed (dirty) X value is host-checked. */
  savedXUrl?: string | null;
}

export const getEditCompanyHeaderValidationSchema = ({
  savedXUrl,
}: EditCompanyHeaderValidationSchemaOptions = {}) =>
  z.object({
    name: z
      .string()
      .max(
        COMPANY_NAME_MAX_LENGTH,
        t('default:maxLengthError', {
          maxLengthValue: COMPANY_NAME_MAX_LENGTH,
        }),
      )
      .optional(),
    linkedinUrl: urlHostValidation(
      LINKEDIN_URL_HOSTS,
      t('companies:company.header.linkedinUrlValidationMessage'),
    )
      .nullable()
      .optional(),
    twitterUrl: urlHostValidation(
      X_URL_HOSTS,
      t('companies:company.header.xUrlValidationMessage'),
      { unchangedValue: savedXUrl ?? '' },
    )
      .nullable()
      .optional(),
    activityStatusId: z.string().nonempty(t`default:fieldIsRequired`),
    ownershipId: z.string().optional(),
    stockExchangeSymbol: z.string().optional(),
    ticker: z.string().optional(),
    ownedBy: z.string().optional(),
    yearFounded: z.number().optional(),
    hqLocation: z
      .object({
        cityId: z.string().nullable(),
        countryId: z.string().nullable(),
        regionId: z.string().nullable(),
      })
      .optional()
      .nullable(),
    employeesRangeId: z.string().optional(),
    websiteUrl: z.string().min(1, t`default:fieldIsRequired`),
    keyOffering: z
      .string()
      .max(
        KEY_OFFERING_MAX_LENGTH,
        t('default:maxLengthError', {
          maxLengthValue: KEY_OFFERING_MAX_LENGTH,
        }),
      )
      .optional(),
    formerName: z.string().optional(),
    impacts: z.array(z.string()).optional(),
    logo: z.custom<File | null>(val => val === null || val instanceof File),
  });
